import { useState } from 'react';
import { useGameProfileVerificationStart } from '../hooks/useGameProfileVerificationStart.js';

export function DesktopGameProfileVerification() {
  const verification = useGameProfileVerificationStart();
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    if (!verification.challenge?.verificationCode) return;

    try {
      await navigator.clipboard.writeText(verification.challenge.verificationCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const resetOrReissue = () => {
    if (verification.expired && verification.hasSavedProfile) {
      verification.reverifyCurrentProfile();
      return;
    }
    verification.resetChallenge();
  };

  return (
    <section className="desktop-game-profile-panel" aria-label="BlablaLink 게임 프로필 인증">
      <div className="desktop-game-profile-heading">
        <div>
          <p className="section-eyebrow">BLABLALINK VERIFICATION</p>
          <h2>게임 프로필 연결</h2>
          <p>프로필 URL과 일회용 상태 메시지 코드로 본인 게임 계정을 확인합니다.</p>
        </div>
        <span className="desktop-game-profile-step">03</span>
      </div>

      {verification.profileLoading ? (
        <p className="desktop-game-profile-notice checking" role="status">
          연결된 게임 프로필을 확인하고 있습니다…
        </p>
      ) : verification.verifiedProfile ? (
        <div className="desktop-game-profile-verified" role="status">
          <div className="desktop-game-profile-verified-mark" aria-hidden="true">✓</div>
          <div>
            <span>BLABLALINK VERIFIED</span>
            <h3>{verification.verifiedProfile.nickname || '닉네임 확인 완료'}</h3>
            <dl>
              <div>
                <dt>유니온</dt>
                <dd>{verification.verifiedProfile.guildName || '미가입 또는 확인되지 않음'}</dd>
              </div>
              <div>
                <dt>인증 상태</dt>
                <dd>게임 프로필 연결 완료</dd>
              </div>
            </dl>
            {verification.checkNotice.message ? (
              <p className={`desktop-game-profile-notice ${verification.checkNotice.type || 'success'}`}>
                {verification.checkNotice.message}
              </p>
            ) : null}
            <button
              className="desktop-game-profile-check"
              type="button"
              onClick={verification.reverifyCurrentProfile}
              disabled={verification.submitting}
            >
              {verification.submitting ? '재인증 코드 발급 중…' : '저장된 BlablaLink로 다시 인증'}
            </button>
          </div>
        </div>
      ) : !verification.challenge ? (
        <form className="desktop-game-profile-form" onSubmit={verification.submit}>
          <label>
            <span>BlablaLink 프로필 URL</span>
            <input
              type="url"
              value={verification.profileUrl}
              onChange={(event) => verification.setProfileUrl(event.target.value)}
              placeholder="https://www.blablalink.com/user?openid=..."
              autoComplete="url"
              disabled={verification.submitting}
              required
            />
          </label>

          {verification.notice.message ? (
            <p className={`desktop-game-profile-notice ${verification.notice.type}`} role="status">
              {verification.notice.message}
            </p>
          ) : null}

          <button type="submit" disabled={verification.submitting}>
            {verification.submitting ? '코드 발급 중…' : '인증 코드 발급'}
          </button>
        </form>
      ) : (
        <div className="desktop-game-profile-challenge">
          <div className="desktop-game-profile-code-block">
            <span>인증 코드</span>
            <strong>{verification.challenge.verificationCode}</strong>
            <button type="button" onClick={copyCode}>{copied ? '복사됨' : '코드 복사'}</button>
          </div>

          <div className={`desktop-game-profile-timer ${verification.expired ? 'expired' : ''}`}>
            <span>{verification.expired ? '코드 만료' : '코드 남은 시간'}</span>
            <strong>{verification.formattedRemaining}</strong>
          </div>

          <ol className="desktop-game-profile-steps">
            <li>{verification.hasSavedProfile ? '기존에 연결된 BlablaLink 프로필' : 'BlablaLink 프로필'}의 상태 메시지 편집 화면을 엽니다.</li>
            <li>위 인증 코드를 상태 메시지에 그대로 입력하고 저장합니다.</li>
            <li>아래 버튼을 한 번 누르면 최대 2분 30초 동안 자동으로 확인합니다.</li>
          </ol>

          {verification.checking ? (
            <div className="desktop-game-profile-check-progress" role="status" aria-live="polite">
              <span className="desktop-game-profile-spinner" aria-hidden="true" />
              <div>
                <strong>BlablaLink 불러오는 중</strong>
                <p>{verification.checkNotice.message}</p>
              </div>
              <div className="desktop-game-profile-check-meta">
                <span>자동 확인 남은 시간</span>
                <strong>{verification.formattedCheckRemaining}</strong>
                <small>{verification.checkAttemptCount ? `${verification.checkAttemptCount}회 확인` : '첫 확인 중'}</small>
              </div>
            </div>
          ) : verification.checkNotice.message ? (
            <p className={`desktop-game-profile-notice ${verification.checkNotice.type}`} role="status">
              {verification.checkNotice.message}
            </p>
          ) : null}

          <div className="desktop-game-profile-actions">
            <button
              className="desktop-game-profile-check"
              type="button"
              onClick={verification.checking ? verification.cancelChecking : verification.startChecking}
              disabled={verification.expired}
            >
              {verification.checking ? '자동 확인 중지' : '상태 메시지 저장 완료 · 인증 시작'}
            </button>
            <button
              className="desktop-game-profile-reissue"
              type="button"
              onClick={resetOrReissue}
              disabled={verification.checking || verification.submitting}
            >
              {verification.expired
                ? (verification.hasSavedProfile ? '저장된 프로필로 새 코드 발급' : '새 코드 발급하기')
                : '인증 취소'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
